<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'

const containerRef = ref<HTMLDivElement | null>(null)

const props = defineProps<{
  message: string
  retryLabel?: string
  title?: string
  variant?: 'error' | 'warning' | 'network'
  showFallbackNotice?: boolean
  compact?: boolean
  isRetrying?: boolean
}>()

defineEmits<{
  retry: []
}>()

const errorTitle = computed(() => {
  if (props.title) return props.title
  if (props.variant === 'network') return 'Connection Problem'
  if (props.variant === 'warning') return 'Something went wrong'
  return 'Unable to load data'
})

const userFriendlyMessage = computed(() => {
  const msg = props.message.toLowerCase()
  if (msg.includes('network') || msg.includes('fetch') || msg.includes('failed to fetch')) {
    return 'Unable to connect to the server. Please check your internet connection and try again.'
  }
  if (msg.includes('timeout')) {
    return 'The request took too long. The server might be busy. Please try again.'
  }
  if (msg.includes('404') || msg.includes('not found')) {
    return 'The requested data could not be found. It may have been moved or deleted.'
  }
  if (msg.includes('500') || msg.includes('server error')) {
    return 'The server encountered an error. Please try again later.'
  }
  if (msg.includes('401') || msg.includes('unauthorized')) {
    return 'You are not authorized to access this data.'
  }
  return props.message
})

onMounted(() => {
  containerRef.value?.focus()
})
</script>

<template>
  <div
    ref="containerRef"
    tabindex="-1"
    :class="[
      'rounded-lg border focus-ring focus-ring-error',
      compact ? 'p-4' : 'card py-8',
      variant === 'warning' ? 'border-yellow-600 bg-yellow-900/20' : 'border-red-600 bg-red-900/20'
    ]"
    role="alert"
    aria-live="assertive"
  >
    <div :class="['flex', compact ? 'items-center gap-4' : 'flex-col items-center text-center']">
      <div :class="compact ? '' : 'mb-4'">
        <svg
          v-if="variant === 'network'"
          class="w-10 h-10 text-red-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
          />
        </svg>
        <svg
          v-else
          :class="['w-10 h-10', variant === 'warning' ? 'text-yellow-400' : 'text-red-400']"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <div :class="compact ? 'flex-1' : ''">
        <p role="heading" aria-level="2" :class="['font-semibold', compact ? 'text-base' : 'text-lg mb-2', variant === 'warning' ? 'text-yellow-400' : 'text-red-400']">
          {{ errorTitle }}
        </p>
        <p :class="['text-gray-300', compact ? 'text-sm' : 'mb-4']">{{ userFriendlyMessage }}</p>
        <p v-if="showFallbackNotice" class="text-gray-300 text-sm mt-2">
          Showing cached data. Some information may be outdated.
        </p>
      </div>
      <button
        @click="$emit('retry')"
        :disabled="isRetrying"
        :aria-disabled="isRetrying"
        :class="[
          'btn',
          compact ? 'btn-sm' : '',
          variant === 'warning' ? 'btn-warning' : 'btn-primary'
        ]"
      >
        <svg
          v-if="isRetrying"
          class="animate-spin -ml-1 mr-2 h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        {{ isRetrying ? 'Retrying...' : (retryLabel || 'Try Again') }}
      </button>
    </div>
  </div>
</template>
